<style>
  input {
    border: none;
    background: none;
    font-size: inherit;
    color: inherit;
    font-weight: inherit;
    text-align: inherit;
    box-shadow: none;
  }
</style>

<script context="module" lang="ts">
  export const currentEventData: Writable<null | UserEventData['data']> =
    writable(null);

  let ffComputedColumns = false;
</script>

<script lang="ts">
  import { page } from '$app/stores';
  import { PUBLIC_BETWATCH_API_ENDPOINT } from '$env/static/public';
  import type { LayoutPreference, Runner, UserEventData } from '$types/races';
  import { selectedRaceId } from '$utils/navigation';
  import type { MarketMap } from '$utils/race';
  import { onMount } from 'svelte';
  import { get, writable, type Writable } from 'svelte/store';

  export let marketMapping: MarketMap;

  export let runner: Runner;
  export let layoutPreference: LayoutPreference;
  let value = '';
  let displayedValue = value;
  let editing = false,
    original = '';

  // track the value to displayedValue
  $: {
    if (!value.startsWith('=')) {
      displayedValue = value;
    } else {
      // computed columns
      if (!ffComputedColumns) {
        displayedValue = value;
      } else {
        try {
          let formula = value.slice(1);
          // handle any * or / or + or - in the formula
          // anything that starts with a $ is a reference to another bookmaker price
          // iterate through the formula and replace any $ with the value from the bookmaker
          // if the bookmaker doesn't exist, then replace with 0
          // then eval the formula
          // iterate over any $ (not numbers)
          const bookmakerIds = formula.match(/\$[a-zA-Z]+/g);
          if (bookmakerIds) {
            for (let i = 0; i < bookmakerIds.length; i++) {
              const element = bookmakerIds[i].slice(1);
              const bookmakerMarket =
                marketMapping.getBookmarketMarketFromRunner(runner, element);
              if (bookmakerMarket) {
                formula = formula.replace(
                  bookmakerIds[i],
                  get(bookmakerMarket).fixedWin.price.toString()
                );
              } else {
                formula = formula.replace(bookmakerIds[i], '0');
              }
            }
          }

          // eval the formula
          displayedValue = eval(formula);
        } catch (e) {
          displayedValue = value;
        }
      }
    }
  }

  $: ({ session, streamed } = $page.data);
  $: currentData = streamed?.userEventData?.data?.userEventData.data ?? null;
  $: {
    if (currentData && !$currentEventData) currentEventData.set(currentData);
  }

  //   pull the current value from the userEventData
  onMount(() => {
    if (currentData) {
      const matchedData = currentData?.find(
        (d) => d.columnName === layoutPreference.name
      );
      const selectionData = matchedData?.selectionData.filter(
        (d) => d.selectionId === runner.id
      );
      if (selectionData && (selectionData?.length ?? 0) > 1) {
        console.error('More than one selection data found');
        // filter out
        currentData = currentData?.map((d) => {
          if (d.columnName === layoutPreference.name) {
            return {
              ...d,
              selectionData: d.selectionData.filter(
                (sd) => sd.selectionId !== runner.id
              )
            };
          }
          return d;
        });
      }
      value =
        currentData
          ?.find((d) => d.columnName === layoutPreference.name)
          ?.selectionData.find((d) => d.selectionId === runner.id)?.value ?? '';
    }
    original = value;

    input.blur();
  });

  let input: HTMLInputElement;
  async function updateUserEventData() {
    currentEventData.set(currentData);
    const headers = {
      'Content-Type': 'application/json'
    } as Record<string, string>;
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session?.access_token}`;
    } else {
      throw new Error('No auth provided');
    }

    await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query: `mutation UpdateUserEventData($input: UpdateUserEventDataInput!) {
          updateUserEventData(input: $input) {
            eventId
            data {
              columnName
              selectionData {
                selectionId
                value
              }
            }
          }
        }`,
        variables: {
          input: {
            eventId: $selectedRaceId,
            customData: currentData
          }
        }
      })
    }).catch((e) => {
      console.error(e);
    });
  }

  async function handleChange() {
    if (!currentData?.find((d) => d.columnName === layoutPreference.name)) {
      if (!layoutPreference.name) {
        console.error('No layout preference name provided');
        return;
      }
      currentData?.push({
        columnName: layoutPreference.name,
        selectionData: []
      });
    }

    const columnData = currentData?.find(
      (d) => d.columnName === layoutPreference.name
    );

    // update the value if it exists, or push
    if (columnData?.selectionData.find((d) => d.selectionId === runner.id)) {
      const selectionData = columnData.selectionData.find(
        (d) => d.selectionId === runner.id
      );
      if (selectionData) {
        selectionData.value = value;
      }
    } else {
      columnData?.selectionData.push({
        selectionId: runner.id,
        value
      });
    }
    await updateUserEventData();
    // editing = false;
  }

  function edit() {
    editing = true;
    // focus input
    setTimeout(() => {
      input.focus();
    }, 10);
  }

  function submit() {
    if (value != original) {
      handleChange();
    }

    editing = false;
  }

  function keydown(event: KeyboardEvent) {
    if (event.key == 'Escape') {
      event.preventDefault();
      value = original;
      editing = false;
    }
  }
</script>

<td
  class="relative table-cell cursor-text whitespace-nowrap border bg-stone-100 text-center text-sm font-semibold text-gray-900"
>
  {#if !editing}
    <div
      role="button"
      tabindex="0"
      on:click={edit}
      on:keypress={edit}
      on:focus={edit}
      hidden={editing}
      class="absolute inset-0 flex h-full w-full items-center justify-center rounded-sm border-0 bg-stone-100 p-0 text-center text-[13px]"
    >
      {displayedValue}
    </div>
  {/if}

  <form>
    <input
      on:keydown={keydown}
      bind:this={input}
      bind:value
      on:blur={submit}
      type="text"
      class="w-{(layoutPreference?.size ?? 'SMALL') === 'SMALL'
        ? 8
        : layoutPreference?.size === 'MEDIUM'
          ? 24
          : 56} rounded-sm border-0 bg-slate-100 p-0 text-center text-[13px] focus:bg-purple-100"
    />
  </form>
</td>
